/** @jsxImportSource @emotion/react */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { Button } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { MenuContext } from 'App';
import { MenuContextType } from 'models/admin/Menu';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import { tabs } from 'components/layouts/Tab';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { ItemStatCd, MpItem, MpItemTpCd, TapButtonType } from 'models/mp/MpItem';
import PropTypes from 'prop-types';
import { ManagementMode } from 'models/common/Common';
import {
  findMpItem,
  generateItemId,
  saveIprvClsf,
  saveMpItem,
  saveMpItemStat,
  switchToEc,
} from 'apis/mp/MpItem';
import { findMpResults, saveMpResult } from 'apis/mp/MpResult';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import MpItemInfoContent from './itemmanagement/MpItemInfoContent';
import MpItemOtherContent from './itemmanagement/MpItemOtherContent';
import MpItemTemplatePopUp from './popup/MpItemTemplatePopUp';
import { ControlBtnGroup } from 'components/layouts/ContentLayout';
import { ComboBox } from 'components/selects/ComboBox';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import useSessionStore from 'stores/useSessionStore';
import { getTranslatedText } from '../../apis/admin/Translation';
import { Translation } from '../../models/admin/Translation';
import MpItemStatHisModal from 'components/modals/mp/MpItemStatHisModal';
import MpItemCopModal from 'components/modals/mp/MpItemCopModal';
import { GatingContListPopUp } from '../gtng/popup/GatingContListPopUp';
import { MpRemarkPopUp } from './popup/MpRemarkPopUp';
import { MpIprvPurpChgPopUp } from './popup/MpIprvPurpChgPopUp';
import { existMpItemCop } from '../../apis/mp/MpItemCop';
import { MpMgrChgHistoryPopUp } from './popup/MpMgrChgHistoryPopUp';
import UserMultiModal from '../../components/modals/common/UserMultiModal';
import { MpResult } from 'models/mp/MpResult';
import { FindInputBtnSet, TextBtnSet } from '../../components/layouts/TableInButton';
import MpItemStatProcess from './base/MpItemStatProcess';
import { hasRole } from 'utils/SessionUtil';
import { SubTitleGroup } from 'components/layouts/ContentLayout';
import { CommonUtil } from 'utils/CommonUtil';

/**
 * @description mp item 등록 page
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  25.11.05    jihwan89       수평전개탭에서 설비변경점탭으로 이동시 다시 수평전개탭으로 돌아가는 오류 수정
 *  25.11.12    jihwan89       설비변경점 개선사항 5.2 제작사양서 해당사항없음 선택 시 FMD IF 연계하지 않고 완료처리하기 위해 도면 saveMpItemStat에 mpDrwList추가
 *  25.11.19    jihwan89       설비변경점 개선사항 5.2 Fast Track 시 기본정보 수평전개로 복사 (법인, 팩토리, 담당자->수평전개담당자, 설비(개조개선담당자제외))
 *  26.02.05    jihwan89       수평전개 진척현황에서 대표호기 클릭시 설비변경점 탭으로 이동
 */

interface DisplayCondition {
  reqMgrNm?: string; // 기술제안자명
  dataInsUserNm?: string; // 등록자명
}

interface PageLocationState {
  itemId?: string;
  mode?: ManagementMode;
  itemTpCd?: string;
  copCd?: string;
  plntCd?: string;
  lnId?: string;
  copyTarget?: MpItem;
  plntRepEquip?: string;
}

const MpItemManagementPage = () => {
  const { t } = useTranslation();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { openLoading } = useLoading();
  const userSession = useSessionStore();
  const firstRef = useRef(true);
  const infoRef = useRef<any>(null);
  const otherRef = useRef<any>(null);
  const statRef = useRef<any>(null);
  const [errors, setErrors] = useState<any>({});
  const menuContext = useContext<MenuContextType>(MenuContext);
  const [searchParams] = useSearchParams();
  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);
  const [mpItem, setMpItem] = useState<MpItem>({});
  const [code, setCode] = useState<any>({});
  const [displayCondition, setDisplayCondition] = useState<DisplayCondition>({});
  const [isOpenElmEmpPopup, setOpenElmEmpPopup] = useState<boolean>(false);

  const [isOpenRemarkPopup, setOpenRemarkPopup] = useState<boolean>(false);
  const [remarkPopupCondition, setRemarkPopupCondition] = useState<any>({});
  const [isOpenMpMgrChgHistoryPopup, setOpenMpMgrChgHistoryPopup] = useState<boolean>(false);

  const [isOpenIprvPurpChgPopup, setOpenIprvPurpChgPopup] = useState<boolean>(false);
  const [isOpenSwitchToEcPopup, setOpenSwitchToEcPopup] = useState<boolean>(false); // 개선목적변경팝업 (EC전환)

  const [isMpItemTplPopup, setMpItemTplPopup] = useState<boolean>(false);
  const refLangCds = ['ko', 'en', 'zh'];
  const [refLangCd, setRefLangCd] = useState<string>('');
  const [isFastTrack, setFastTrack] = useState<boolean>(false);
  const [isMpItemStatHisModalOpen, setMpItemStatHisModalOpen] = useState<boolean>(false);
  const [isOpenMpItemCopModal, setOpenMpItemCopModal] = useState<boolean>(false);
  const [isOpenUserReadModal, setOpenUserReadModal] = useState<boolean>(false);

  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [changeTab, setChangeTab] = useState<boolean>(false);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const [hasIfAuth, setHasIfAuth] = useState<boolean>(false);

  const isShowRmk = (column = '') => {
    // 신규개선
    if (MpItemTpCd.NI === mpItem?.mpItemTpCd) {
      // 신규등록인 경우 담당자 사유 입력칸 미노출
      if (!mpItem?.itemStatCd) {
        return false;
      } else {
        // (cf. isEditable로 조건에 따라 팝업하므로 조건 간소화)
        return true;
      }
    }
    // 설비변경점
    if (MpItemTpCd.MP === mpItem?.mpItemTpCd) {
      // (cf. isEditable로 조건에 따라 팝업하므로 조건 간소화)
      return true;
    }
  };

  const isRequireRmk = (column = '') => {
    if (isShowRmk(column)) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * 편집가능여부
   * @param column
   * @param fstrYn  FastTrack여부 (기본사항에서 FastTrack여부가 변경된 경우 변경된 값 적용해서 판단하기 위함)
   */
  const isEditable = (column = '', fstrYn = 'N') => {
    // 신규개선
    if (MpItemTpCd.NI === mpItem?.mpItemTpCd) {
      // 신규등록인 경우
      if (!mpItem?.itemStatCd) {
        // 시스템 자동 외 모두
        return true;
      }
      // 개발중인 경우
      if (ItemStatCd.Develop === mpItem?.itemStatCd) {
        // Item명, 담당자, 개선목적, Fast Track 여부, 공장, 대상설비, 변경전/후 내용, 첨부파일(전체)
        const cols = [
          'itemEngNm',
          'itemNm',
          'itemCngNm',
          'reqMgrId',
          'iprvClsfLv1Cd',
          'iprvClsfLv2Cd',
          'iprvClsfLv3Cd',
          'fstrYn',
          'tgtPlntCd',
          'equipmentName',
          'oldRevCtn',
          'newRevCtn',
          'attach',
        ].includes(column);
        return cols && (mpItem?.reqMgrYn === 'Y' || mpItem?.mcrcYn === 'Y' || hasAuth);
      }
      // 분과위 심의중인 경우
      if (ItemStatCd.McrcReview === mpItem?.itemStatCd) {
        // Item명, 담당자, 개선목적, Fast Track 여부, 공장, 대상설비, 변경전/후 내용, 첨부파일(전체)
        const cols = [
          'itemEngNm',
          'itemNm',
          'itemCngNm',
          'reqMgrId',
          'iprvClsfLv1Cd',
          'iprvClsfLv2Cd',
          'iprvClsfLv3Cd',
          'fstrYn',
          'tgtPlntCd',
          'equipmentName',
          'oldRevCtn',
          'newRevCtn',
          'attach',
        ];
        // Fast Track인 경우
        if (fstrYn === 'Y' || mpItem?.fstrYn === 'Y') {
          // 사양서 담당자
          cols.push('chgCrgeUserId');
        }
        return cols.includes(column) && (mpItem?.mcrcYn === 'Y' || hasAuth);
      }
    }
    // 설비변경점
    if (MpItemTpCd.MP === mpItem?.mpItemTpCd) {
      // Item 등록인 경우
      if (ItemStatCd.Regist === mpItem?.itemStatCd) {
        // Item명, 담당자, 공장, 대상설비, 변경전/후 내용, 첨부파일(전체)
        const cols = [
          'itemEngNm',
          'itemNm',
          'itemCngNm',
          'reqMgrId',
          'tgtPlntCd',
          'equipmentName',
          'oldRevCtn',
          'newRevCtn',
          'attach',
        ].includes(column);
        return cols && (mpItem?.reqMgrYn === 'Y' || mpItem?.mcrcYn === 'Y' || hasAuth);
      }
      // 분과위 심의중
      if (ItemStatCd.McrcReview === mpItem?.itemStatCd) {
        // Item명, 담당자, 변경전/후 내용, 첨부파일(전체), 사양서담당자
        const cols = [
          'itemEngNm',
          'itemNm',
          'itemCngNm',
          'reqMgrId',
          'oldRevCtn',
          'newRevCtn',
          'attach',
          'chgCrgeUserId',
        ].includes(column);
        return cols && (mpItem?.mcrcYn === 'Y' || hasAuth);
      }
    }
    return false;
  };

  const tabMenus = useMemo(() => {
    let tabs = [{ id: 'INFO', name: t('mp.label.설비변경점', '설비변경점') }];
    // [25.10.06 - 해당 조건 삭제(설비변경점_개선요구사항_v5.2 - 58행)][25.06.16] 신규개선인 경우에도 Fast Track 여부를 Y로 설정한 경우에 수평전개 노출
    if (locationState?.itemId && ManagementMode.MODIFY === locationState?.mode) {
      if (
        // isFastTrack || // [25.10.06] Fast Track 이라도 바로 보여주지않고 수평전개, 설비변경점/수평전개 단계 이후인 경우 노출 (설비변경점_개선요구사항_v5.2 - 58행)
        MpItemTpCd.HE === mpItem?.mpItemTpCd ||
        MpItemTpCd.MPHE === mpItem?.mpItemTpCd
      ) {
        tabs = [...tabs, { id: MpItemTpCd.HE, name: t('mp.label.수평전개', '수평전개') }];
      }
    }
    return tabs;
  }, [locationState?.itemId, locationState?.mode, isFastTrack, mpItem?.mpItemTpCd, hasAuth]);

  const mpItemTpCdNm = useMemo(() => {
    const cd = (code?.mpItemTpCd || []).filter((o) => o.cmnCd === mpItem?.mpItemTpCd);
    return cd.length ? cd[0]?.cmnCdNm : undefined;
  }, [code?.mpItemTpCd, mpItem?.mpItemTpCd]);

  const getCommonCodes = async () => {
    const mpClsfCd: Code[] = await getCommonCodeNames('MP_CLSF_CD'); // MP분류
    const mpItemTpCd: Code[] = await getCommonCodeNames('MP_ITEM_TP_CD');
    const mpItemStatMap: Code[] = await getCommonCodeNames('MP_ITEM_STAT_MAP');
    const langCd: Code[] = await getCommonCodeNames('LANG_CD');
    setCode({
      mpClsfCd: mpClsfCd,
      mpItemTpCd: mpItemTpCd,
      mpItemStatMap: mpItemStatMap,
      langCd: langCd
        .filter((o) => refLangCds.includes(o?.cmnCd || ''))
        .reduce(
          (acc, cur) => [
            ...acc,
            {
              ...cur,
              cmnCdNm: cur.optValCtn1 || cur.cmnCdNm,
            },
          ],
          [] as Code[]
        ),
    });
  };

  const handleOpenRemarkPopup = (type: 'DROP' | 'DEV_REVIEW' | 'DLBR_CPLT' | 'HE_REPROCESS') => {
    const title =
      type === 'DROP'
        ? t('mp.label.Drop', 'Drop')
        : type === 'DEV_REVIEW'
        ? t('mp.label.개발재검토', '개발재검토')
        : type === 'DLBR_CPLT'
        ? t('mp.label.수평전개 진행/보류 선택', '수평전개 진행/보류 선택')
        : type === 'HE_REPROCESS'
        ? t('mp.label.수평전개 재진행', '수평전개 재진행')
        : '사유입력';
    setRemarkPopupCondition({
      title: title,
      type: type,
      rmk: '',
      typeValue: '', // 수평전개 진행/보류 유형 기본값
    });
    setOpenRemarkPopup(true);
  };

  const renderTabButtons = (type: TapButtonType) => {
    switch (type) {
      case TapButtonType.ButtonSave:
        return (
          <Button css={IconButton.button} className="save" onClick={handleSave} disableRipple>
            {t('com.button.저장', '저장')}
          </Button>
        );
      case TapButtonType.ButtonM4Change:
        return (
          <Button
            css={IconButton.button}
            className="chevron"
            onClick={() => {
              handleChangeStat(TapButtonType.ButtonM4Change);
            }}
            disableRipple
          >
            {t('mp.label.4M 변경 확정', '4M 변경 확정')}
          </Button>
        );
      case TapButtonType.ButtonIprvClsfChange:
        return (
          <Button
            css={IconButton.button}
            className="rule"
            onClick={() => {
              setOpenIprvPurpChgPopup(true);
            }}
            disableRipple
          >
            {t('mp.label.개선목적 변경', '개선목적 변경')}
          </Button>
        );
      case TapButtonType.ButtonReviewComplete:
        return (
          <Button
            css={IconButton.button}
            className="chevron"
            onClick={() => {
              if (infoRef.current?.validateItemStatCd(ItemStatCd.Regist)) {
                handleOpenRemarkPopup('DLBR_CPLT');
              }
            }}
            disableRipple
          >
            {t('mp.label.심의완료', '심의완료')}
          </Button>
        );
      case TapButtonType.ButtonHeReprocess:
        return (
          <Button
            css={IconButton.button}
            className="reception"
            onClick={() => handleOpenRemarkPopup('HE_REPROCESS')}
            disableRipple
          >
            {t('mp.label.수평전개진행', '수평전개진행')}
          </Button>
        );
      case TapButtonType.ButtonDev:
        return (
          <Button
            css={IconButton.button}
            className="develop"
            onClick={() => handleOpenRemarkPopup('DEV_REVIEW')}
            disableRipple
          >
            {t('mp.label.개발재검토', '개발재검토')}
          </Button>
        );
      case TapButtonType.ButtonItemRegist:
        return (
          <Button
            css={IconButton.button}
            className="regist"
            onClick={() => handleChangeStat(TapButtonType.ButtonItemRegist)}
            disableRipple
          >
            {mpItem?.mpItemTpCd === MpItemTpCd.NI
              ? t('mp.label.심의완료', '심의완료')
              : 'Item 등록'}
          </Button>
        );
      case TapButtonType.ButtonDevComp:
        return (
          <Button
            css={IconButton.button}
            className="chevron"
            onClick={() => handleChangeStat(TapButtonType.ButtonDevComp)}
            disableRipple
          >
            {t('com.button.개발완료', '개발완료')}
          </Button>
        );
      case TapButtonType.ButtonDrop:
        return (
          <Button
            css={IconButton.button}
            className="developstop"
            onClick={() => handleOpenRemarkPopup('DROP')}
            disableRipple
          >
            {t('com.button.Drop', 'Drop')}
          </Button>
        );
      case TapButtonType.ButtonM4Comp:
        return (
          <Button
            css={IconButton.button}
            className="chevron"
            onClick={() => handleChangeStat(TapButtonType.ButtonDevComp)}
            disableRipple
          >
            4MCC 완료
          </Button>
        );
      case TapButtonType.ButtonM4Drop:
        return (
          <Button
            css={IconButton.button}
            className="developstop"
            onClick={() => handleOpenRemarkPopup('DROP')}
            disableRipple
          >
            4MCC Drop
          </Button>
        );
      case TapButtonType.ButtonCopSet:
        return (
          <Button
            css={IconButton.button}
            className="rule"
            onClick={() => {
              setOpenMpItemCopModal(true);
            }}
            disableRipple
          >
            {t('mp.label.법인설정', '법인설정')}
          </Button>
        );
      case TapButtonType.ButtonEcConv:
        return (
          <Button
            css={IconButton.button}
            className="rule"
            onClick={() => {
              setOpenSwitchToEcPopup(true);
            }}
            disableRipple
          >
            {t('mp.label.EC 전환', 'EC 전환')}
          </Button>
        );
      case TapButtonType.ButtonRvwComp:
        return (
          <Button
            css={IconButton.button}
            className="rule"
            onClick={async () => {
              handleChangeStat(TapButtonType.ButtonRvwComp);
            }}
            disableRipple
          >
            {t('mp.label.검토완료', '검토완료')}
          </Button>
        );
      default:
        return null;
    }
  };

  const mpItemStatGuideTxt = useMemo(() => {
    const stat = (code?.mpItemStatMap || []).filter((o) => {
      // Drop or 완료인 경우 상태만 확인
      if (ItemStatCd.Drop === mpItem?.itemStatCd || ItemStatCd.Complete === mpItem?.itemStatCd) {
        return o.optValCtn2 === mpItem?.itemStatCd;
      }
      return o.optValCtn1 === mpItem?.mpItemTpCd && o.optValCtn2 === mpItem?.itemStatCd;
    });
    if (stat && stat.length > 0) {
      return stat[0].optValCtn5;
    }
    return '';
  }, [code?.mpItemStatMap, mpItem?.mpItemTpCd, mpItem?.itemStatCd]);

  const tabButtons: TapButtonType[] = useMemo(() => {
    const buttons: TapButtonType[] = [];
    // 탭버튼 세팅
    if (currentTabIndex === 0) {
      // 신규개선인 경우
      if (MpItemTpCd.NI === mpItem?.mpItemTpCd) {
        if (!mpItem?.itemStatCd) {
          // 신규등록: <저장>
          buttons.push(TapButtonType.ButtonSave);
        }
        if (ItemStatCd.Develop === mpItem?.itemStatCd) {
          // 개발중_담당자&분과위: <개발완료><Drop><저장>
          //
          if (mpItem?.reqMgrYn === 'Y' || mpItem?.mcrcYn === 'Y' || hasAuth) {
            buttons.push(
              TapButtonType.ButtonDevComp,
              TapButtonType.ButtonDrop,
              TapButtonType.ButtonSave
            );
          }
        }
        if (ItemStatCd.McrcReview === mpItem?.itemStatCd) {
          // 심의중_분과위: <심의완료><개발재검토><Drop><저장>
          if (mpItem?.mcrcYn === 'Y' || hasAuth) {
            buttons.push(
              TapButtonType.ButtonDev,
              TapButtonType.ButtonItemRegist,
              TapButtonType.ButtonDrop,
              TapButtonType.ButtonSave
            );
          }
        }
      }
      // 설비변경점인 경우
      else if (MpItemTpCd.MP === mpItem?.mpItemTpCd) {
        if (ItemStatCd.Regist === mpItem?.itemStatCd) {
          // 1. Item 등록_분과위: <4M 변경 확정><개선목적 변경><Drop><저장>
          if (mpItem?.mcrcYn === 'Y' || hasAuth) {
            buttons.push(
              TapButtonType.ButtonM4Change,
              TapButtonType.ButtonIprvClsfChange,
              TapButtonType.ButtonDrop,
              TapButtonType.ButtonSave
            );
          }
          // 2. Item 등록_담당자: <4M 변경 확정><저장>
          else if (mpItem?.reqMgrYn === 'Y' || hasAuth) {
            buttons.push(TapButtonType.ButtonM4Change, TapButtonType.ButtonSave);
          }
        }
        // TODO 4M 변경중: <Drop> (IF 개발전 임시 사용 - 삭제예정)
        else if (ItemStatCd.M4Change === mpItem?.itemStatCd) {
          if (hasIfAuth) {
            // [25.09.11] DEV, QA환경에서 ADM권한인 경우 노출되도록 수정
            buttons.push(TapButtonType.ButtonM4Comp, TapButtonType.ButtonM4Drop);
          }
        } else if (ItemStatCd.McrcReview === mpItem?.itemStatCd) {
          // 분과위 심의중_분과위: <심의완료><EC 전환><Drop><저장>
          if (mpItem?.mcrcYn === 'Y' || hasAuth) {
            buttons.push(TapButtonType.ButtonReviewComplete);
            // EC인 경우 미노출
            if (mpItem?.ecoYn !== 'Y') {
              buttons.push(TapButtonType.ButtonEcConv);
            }
            buttons.push(TapButtonType.ButtonDrop, TapButtonType.ButtonSave);
          }
        } else if (ItemStatCd.HeHold === mpItem?.itemStatCd) {
          // 수평전개 보류_분과위: <수평전개진행>
          if (mpItem?.mcrcYn === 'Y' || hasAuth) {
            buttons.push(TapButtonType.ButtonHeReprocess);
          }
        }
      }
    }
    // 수평전개 탭인 경우 (설비변경점/수평전개 or 수평전개)
    else {
      /**
       * # 수평전개 탭 내 <법인설정><Factory추가> 버튼
       * 1. <Drop><수평전개보류>를 제외한 모든 상태에서 버튼 노출
       * 2. <수평전개완료><완료>인 경우 담당자/분과위/일반사용자에게 노출
       *       - 그 외 상태는 담당자/분과위인 경우 노출
       */

      // <Drop><수평전개보류>를 제외한 모든 상태에서 버튼 노출
      if (ItemStatCd.Drop !== mpItem?.itemStatCd && ItemStatCd.HeHold !== mpItem?.itemStatCd) {
        // <수평전개완료><완료>인 경우 담당자/분과위/일반사용자에게 노출
        if (
          ItemStatCd.HeComplete === mpItem?.itemStatCd ||
          ItemStatCd.Complete === mpItem?.itemStatCd
        ) {
          buttons.push(TapButtonType.ButtonCopSet);
        } else {
          // 그 외 상태는 담당자/분과위인 경우 노출
          if (mpItem?.reqMgrYn === 'Y' || mpItem?.mcrcYn === 'Y' || hasAuth) {
            buttons.push(TapButtonType.ButtonCopSet);
          }
        }
      }
      // Item 등록
      if (ItemStatCd.Regist === mpItem?.itemStatCd) {
        // 수평전개인 경우 Item 등록: <법인설정><EC 전환><검토완료>
        if (MpItemTpCd.HE === mpItem?.mpItemTpCd) {
          if (mpItem?.reqMgrYn === 'Y' || mpItem?.mcrcYn === 'Y' || hasAuth) {
            if (mpItem?.ecoYn !== 'Y') {
              // EC가 아닌 경우 노출
              buttons.push(TapButtonType.ButtonEcConv);
            }
            buttons.push(TapButtonType.ButtonRvwComp);
          }
        }
        // 설비변경점/수평전개인 경우 Item 등록: <법인설정><개선목적 변경><검토완료>
        else if (MpItemTpCd.MPHE === mpItem?.mpItemTpCd) {
          if (mpItem?.mcrcYn === 'Y' || hasAuth) {
            buttons.push(TapButtonType.ButtonIprvClsfChange, TapButtonType.ButtonRvwComp);
          } // 담당자: <법인설정><검토완료>
          else if (mpItem?.reqMgrYn === 'Y') {
            buttons.push(TapButtonType.ButtonRvwComp);
          }
        }
      } else if (
        ItemStatCd.CopReview === mpItem?.itemStatCd ||
        ItemStatCd.M4Change === mpItem?.itemStatCd
      ) {
        /*
        // [25.09.09] 법인 검토중 (Item 상태)상태 이후로는 검토완료 버튼 삭제
        // 법인 검토중: <법인설정><검토완료>
        // 4M 변경중: <법인설정><검토완료>
        if (mpItem?.reqMgrYn === 'Y' || mpItem?.mcrcYn === 'Y' || hasAuth) {
          buttons.push(TapButtonType.ButtonRvwComp);
        }
        */
      } else if (
        ItemStatCd.HeComplete === mpItem?.itemStatCd ||
        ItemStatCd.Complete === mpItem?.itemStatCd
      ) {
        /*
        // [25.09.09] 법인 검토중 (Item 상태)상태 이후로는 검토완료 버튼 삭제
        // 수평전개완료: <법인설정><검토완료>
        // 완료: <법인설정><검토완료>
        // 일반사용자에게도 버튼 노출
        buttons.push(TapButtonType.ButtonRvwComp);
         */
      }
    }

    //return Array.from(new Set(buttons));
    return buttons;
  }, [
    mpItem.mpItemTpCd,
    mpItem.itemStatCd,
    mpItem.ecoYn,
    mpItem.mcrcYn,
    mpItem.reqMgrYn,
    currentTabIndex,
    hasAuth,
  ]);

  useEffect(() => {
    // 기준언어 기본값 설정
    setRefLangCd(refLangCds.includes(userSession.langCd) ? userSession.langCd : 'en');
    getCommonCodes();
    const appEnv = `${process.env.REACT_APP_NODE_ENV}` || '';
    const testEnv = ['local', 'dev', 'stg'];
    setHasIfAuth(hasRole('ADM') && testEnv.includes(appEnv));
    setHasAuth(hasRole('ADM'));
    if (!locationState?.itemId && ManagementMode.CREATE === locationState?.mode) {
      // item id 생성 (등록)
      generateItemId().then(async (result) => {
        if (!result) {
          openMessageBar({
            type: 'error',
            content: t(
              'mp.msg.Item 번호 생성에 실패했습니다.<br/>새로고침 후 다시 시도해 주세요.',
              'Item 번호 생성에 실패했습니다.<br/>새로고침 후 다시 시도해 주세요.'
            ),
          });
          return;
        }

        // 복사 대상 값 세팅
        if (locationState?.copyTarget) {
          let prevResultList = [] as MpResult[];
          if (
            locationState.copyTarget?.itemId &&
            locationState.copyTarget?.tgtCopCd &&
            locationState.copyTarget?.tgtPlntCd
          ) {
            prevResultList = await findMpResults(
              locationState.copyTarget?.itemId,
              locationState.copyTarget?.tgtCopCd,
              locationState.copyTarget?.tgtPlntCd,
              undefined,
              'N'
            );
          }
          setMpItem({
            reqMgrId: locationState.copyTarget?.reqMgrId, // 담당자ID
            chgCrgeUserId: locationState.copyTarget?.chgCrgeUserId, // 사양서담당자ID
            chgCrgeUserNm: locationState.copyTarget?.chgCrgeUserNm,
            procCd: locationState.copyTarget?.procCd, // 공정
            eqclId: locationState.copyTarget?.eqclId, // 설비군
            iprvClsfLv1Cd: locationState.copyTarget?.iprvClsfLv1Cd, // 개선분류1레벨코드
            iprvClsfLv2Cd: locationState.copyTarget?.iprvClsfLv2Cd,
            iprvClsfLv3Cd: locationState.copyTarget?.iprvClsfLv3Cd,
            ecoYn: locationState.copyTarget?.ecoYn, // ECO여부 (Y: EC, N: NC)
            fstrYn: locationState.copyTarget?.fstrYn, // 패스트트랙여부
            oldRevCtn: locationState.copyTarget?.oldRevCtn, // 변경전개정내용
            newRevCtn: locationState.copyTarget?.newRevCtn, // 변경후개정내용
            mpClsfCd: locationState.copyTarget?.mpClsfCd, // MP분류코드
            tgtCopCd: locationState.copyTarget?.tgtCopCd, // 대상법인코드
            tgtPlntCd: locationState.copyTarget?.tgtPlntCd, // 대상공장코드
            itemNm: locationState.copyTarget?.itemNm,
            itemEngNm: locationState.copyTarget?.itemEngNm,
            itemCngNm: locationState.copyTarget?.itemCngNm,
            resultList: prevResultList,
            // m4ChgNo: locationState.copyTarget?.m4ChgNo, // 4M변경번호
            // m4StatCd: locationState.copyTarget?.m4StatCd,
            // m4StatNm: locationState.copyTarget?.m4StatNm,
            // m4LvNm: locationState.copyTarget?.m4LvNm,
            mpItemTpCd: MpItemTpCd.NI, // MP품목유형코드
            itemStatCd: '',
            itemId: result,
            rawSysNm: 'ELM', // 타시스템과 연계 시 해당 시스템으로 자동설정
            dataInsUserId: userSession.userId,
            dataInsUserNm: userSession.empNm,
            dataUpdUserId: userSession.userId,
            dataUpdUserNm: userSession.empNm,
          });
          setDisplayCondition({
            ...displayCondition,
            reqMgrNm: locationState.copyTarget?.reqMgrNm,
          });
        } else {
          setMpItem({
            mpItemTpCd: MpItemTpCd.NI, // 신규개선
            mpClsfCd: userSession.mpClsfType || '',
            tgtCopCd: userSession.elmCopCd || '',
            itemId: result,
            fstrYn: 'N',
            reqMgrId: userSession.userId,
            rawSysNm: 'ELM', // 타시스템과 연계 시 해당 시스템으로 자동설정
            dataInsUserId: userSession.userId,
            dataInsUserNm: userSession.empNm,
            dataUpdUserId: userSession.userId,
            dataUpdUserNm: userSession.empNm,
          });
          setDisplayCondition((prev) => ({
            ...prev,
            reqMgrNm: userSession.empNm,
          }));
        }
      });
    } else if (searchParams?.get('itemId')) {
      setLocationState((prev) => ({
        ...prev,
        mode: ManagementMode.MODIFY,
        itemId: searchParams?.get('itemId') || '',
      }));
      loadMpItem(searchParams?.get('itemId'), true);
    } else {
      if (!state) {
        openMessageBar({
          type: 'error',
          content: t('mp.msg.Item정보가없습니다.', 'Item정보가없습니다.'),
        });
        menuContext.closeCurrentTab(menuContext);
        return;
      }
    }
  }, []);

  useEffect(() => {
    if (state) {
      // 탭 변경
      changeCurrentTabIndex(state?.itemTpCd || 'INFO');
      if (state?.itemTpCd && state?.copCd) {
        setRefresh(true);
        otherRef.current?.reload(state?.itemId, state?.itemTpCd, state?.copCd, state?.plntCd);
      }
      if (locationState) {
        // 목록에서 다른 항목을 선택한 경우 (신규, 수정 탭이 모두 열려있는 경우 구분하기 위해 조건 추가)
        setLocationState((prev) => (prev?.mode === state?.mode ? state : prev));
      } else {
        setLocationState(state);
      }
    }
  }, [state]);

  const changeCurrentTabIndex = (itemTpCd) => {
    if (itemTpCd && (tabMenus || []).length) {
      itemTpCd = itemTpCd === MpItemTpCd.MPHE ? MpItemTpCd.HE : itemTpCd;
      // 일치하는 유형이 없는 경우 기본사항 탭
      setCurrentTabIndex(
        Math.max(
          0,
          (tabMenus || []).findIndex((o) => o.id === itemTpCd)
        )
      );
    }
  };

  useEffect(() => {
    // TabMenu에 수평전개: MpItemTpCd.HE가 존재하면 수평전개 탭부터 보여준다.
    // 수평전개 진척현황에서 대표호기 클릭시 설비변경점으로 이동(locationState.itemTpCd === 'MP')
    const infoTab = tabMenus?.some((item) => item?.id === MpItemTpCd.HE);
    if (infoTab && locationState.itemTpCd !== 'MP') {
      changeCurrentTabIndex(MpItemTpCd.HE);
    } else {
      changeCurrentTabIndex('INFO'); // 탭이 열린 상태로 번호누르고 들어올 시 탭 오류나는 것을 수정 locationState?.itemTpCd -> 'INFO'로
    }
  }, [locationState?.itemTpCd, tabMenus, state]);

  useEffect(() => {
    if (locationState?.itemId && ManagementMode.MODIFY === locationState?.mode) {
      // 수정 탭이 열린 상태에서 다른 item 선택해서 수정화면 진입하는 경우 재조회
      if (mpItem?.itemId !== locationState?.itemId) {
        firstRef.current = true;
        // 재조회 전에 초기화
        initMpItem();
        loadMpItem(locationState?.itemId, true);
      }
    }
  }, [locationState?.itemId, locationState?.mode]);

  useEffect(() => {
    if (locationState?.itemId) {
      const tabId = tabMenus[currentTabIndex]?.id;
      if (ManagementMode.MODIFY === locationState?.mode && tabId === 'INFO' && !firstRef.current) {
        // item 정보 조회 (수정)
        loadMpItem(locationState?.itemId, true);
      }
      if (tabId === 'HE') {
        // 수평전개 탭 진입 시 법인설정 안된 경우 법인설정 팝업 노출
        existMpItemCop(locationState?.itemId).then((res) => {
          if (res.successOrNot === 'Y' && !res.data) {
            // [25.08.20] 법인설정 권한이 있는 경우에만 진입 시 팝업 노출
            if (
              ItemStatCd.Drop !== mpItem?.itemStatCd &&
              (mpItem?.reqMgrYn === 'Y' || mpItem?.mcrcYn === 'Y' || hasAuth)
            ) {
              setOpenMpItemCopModal(true);
            }
          }
        });
      }
    }
  }, [currentTabIndex, mpItem?.itemStatCd, mpItem?.reqMgrYn, mpItem?.mcrcYn, hasAuth]);

  /**
   * item 정보 조회
   * @param itemId
   * @param isResetTab
   */
  const loadMpItem = (itemId, isResetTab = false) => {
    findMpItem(itemId)
      .then((result) => {
        if (!result) {
          openMessageBar({
            type: 'error',
            content: t('mp.msg.Item정보가없습니다.', 'Item정보가없습니다.'),
          });
          menuContext.closeCurrentTab(menuContext);
          return;
        }

        setMpItem(result);
        setFastTrack(result?.fstrYn === 'Y');

        // [25.11.05] 수평전개탭이 먼저 노출되고 설비변경점 탭을 눌렀을때 수평전개탭으로 돌아가던 오류 수정
        // if (isResetTab) {
        //   if (result?.mpItemTpCd) {
        //     // [25.10.06] 수평전개 Tab 우선 표시 (설비변경점_개선요구사항_v5.2 - 58행)
        //     changeCurrentTabIndex(result?.mpItemTpCd);
        //   } else if (!locationState?.itemTpCd) {
        //     // MP분류가 없는 경우에만 기본사항 탭으로 변경
        //     setCurrentTabIndex(0);
        //   }
        // }

        setDisplayCondition({
          ...displayCondition,
          reqMgrNm: result.reqMgrNm,
        });

        infoRef?.current?.handleItemChange('itemStatCd', result.itemStatCd);
        infoRef?.current?.refresh(result);
      })
      .finally(() => {
        if (firstRef.current) {
          firstRef.current = false;
        }
      });
  };

  const regexItemNm = (itemNm, isEng = false) => {
    const regexEng = /[^a-zA-Z0-9 ~,!.@/_#$%^&*(){[}\]+=?]+/g;
    const regexEtc =
      /[^a-zA-Z0-9 가-힇ㄱ-ㅎㅏ-ㅣぁ-ゔァ-ヴー々〆〤一-龥~,!.@/_#$%^&*()（）{[}\]+=?]+/g;
    // [25.09.16] 중국어의 경우 소괄호 ( 번역 시  특수문자（ 로 번역되고, 띄어쓰기 사라져서 오는 경우가 있음
    return (itemNm || '').replace(isEng ? regexEng : regexEtc, '');
  };

  const handleItemChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setMpItem((prev) => ({
      ...prev,
      [e.target.name]: ['itemEngNm', 'itemNm', 'itemCngNm'].includes(e.target.name)
        ? regexItemNm(e.target.value, e.target.name === 'itemEngNm')
        : e.target.value,
    }));

    setErrors((prev) => ({
      ...prev,
      [e.target.name]: false,
    }));
  };

  const initMpItem = () =>
    setMpItem({
      itemId: '',
      itemNm: '',
      itemEngNm: '',
      reqMgrId: '',
      mpClsfCd: '',
      mpYn: '',
      heYn: '',
      niYn: '',
      dataInsUserNm: '',
      dataInsDtm: '',
      dataUpdUserNm: '',
      dataUpdDtm: '',
    });

  const handleDisplayConditionChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const name = e.target.name;
    setDisplayCondition((prev) => ({
      ...prev,
      [name]: e.target.value,
    }));

    setMpItem((prev) => ({
      ...prev,
      [name.replace('Nm', 'Id')]: '',
    }));
  };

  const validate = () => {
    const err = {};
    // Item 명(표준)
    if (!mpItem?.itemEngNm) {
      Object.assign(err, { itemEngNm: true });
    }
    // MP분류
    if (!mpItem?.mpClsfCd) {
      Object.assign(err, { mpClsfCd: true });
    }
    // 담당자
    if (!mpItem?.reqMgrId) {
      Object.assign(err, { reqMgrId: true });
    }

    setErrors(err);
    if (!Object.keys(err).filter((k) => err[k]).length) {
      return true;
    }
    return false;
  };

  const handleSave = () => {
    if (validate()) {
      const tabId = tabMenus[currentTabIndex].id;
      const defaultParam = {
        itemId: mpItem?.itemId,
        itemNm: mpItem?.itemNm,
        itemEngNm: mpItem?.itemEngNm,
        itemCngNm: mpItem?.itemCngNm,
        reqMgrId: mpItem?.reqMgrId,
        reqMgrChgRsnCtn: mpItem?.reqMgrChgRsnCtn,
        mpClsfCd: mpItem?.mpClsfCd,
        mpItemTpCd: mpItem?.mpItemTpCd,
        itemStatCd: mpItem?.itemStatCd,
      } as MpItem;

      // 기본사항 탭인 경우
      if (tabId === 'INFO') {
        if (!infoRef.current.validate()) {
          return;
        }
        openCommonModal({
          modalType: 'confirm',
          content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
          yesCallback: () => {
            openLoading(true);

            const item = infoRef.current.getMpItem();
            const param = {
              ...item,
              ...defaultParam,
            };

            setMpItem(param);

            saveMpItem(param)
              .then((res) => {
                openMessageBar({
                  type: res.successOrNot === 'Y' ? 'confirm' : 'error',
                  content:
                    res.successOrNot === 'Y'
                      ? t('com.label.저장되었습니다.', '저장되었습니다.')
                      : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
                });
                if (res.successOrNot === 'Y') {
                  if (locationState?.mode === ManagementMode.CREATE) {
                    menuContext.closeCurrentTab(menuContext);
                    setTimeout(() => {
                      navigate(`/mp/item-management`, {
                        replace: true,
                        state: {
                          upprMnuUrl: '/mp/item',
                          mode: ManagementMode.MODIFY,
                          itemId: param.itemId,
                          mnuId: `${location.pathname}/${ManagementMode.MODIFY}`,
                          mnuNm: t('mp.label.Item 수정', 'Item 수정'),
                        },
                      });
                    }, 100);
                  } else {
                    setLocationState((prev) => ({
                      ...prev,
                      mode: ManagementMode.MODIFY,
                      itemId: param.itemId,
                    }));
                    statRef.current?.refresh(mpItem.itemId);
                    loadMpItem(param.itemId, true);
                  }
                }
              })
              .finally(() => openLoading(false));
          },
        });
      } else {
        if (!otherRef.current.validate()) {
          return;
        }

        const param = {
          ...defaultParam,
          itemTpCd: tabId,
        };

        openCommonModal({
          modalType: 'confirm',
          content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
          yesCallback: () => {
            saveMpResult(param, otherRef.current.getRowData()).then((res) => {
              openMessageBar({
                type: res.successOrNot === 'Y' ? 'confirm' : 'error',
                content:
                  res.successOrNot === 'Y'
                    ? t('com.label.저장되었습니다.', '저장되었습니다.')
                    : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
              });
              setLocationState((prev) => ({
                ...prev,
                mode: ManagementMode.MODIFY,
                itemId: param.itemId,
              }));
              loadMpItem(param.itemId);
              otherRef.current.refresh();
            });
          },
        });
      }
    }
  };

  const handleChangeStat = async (stat, rsnCtn = '') => {
    // 특화로직 추가
    const valid = true;
    let msg = '';
    if (!mpItem?.itemId || !mpItem?.mpItemTpCd) {
      openMessageBar({
        type: 'error',
        content:
          t('com.label.잘못된 접근입니다.', '잘못된 접근입니다.') +
          <br /> +
          t('com.label.새로고침 후 다시 시도해 주세요.', '새로고침 후 다시 시도해 주세요.'),
      });
      return;
    }

    // 기본사항 탭 검증
    if (currentTabIndex === 0 && !infoRef.current?.validateItemStatCd(stat)) {
      return;
    }
    // 수평전개 탭 검증
    if (currentTabIndex === 1 && !(await otherRef.current?.validateItemStatCd(stat))) {
      return;
    }
    if (MpItemTpCd.NI === mpItem?.mpItemTpCd) {
      // 신규개선인 경우
      if (ItemStatCd.Develop === stat) {
        if (!rsnCtn) {
          openMessageBar({
            type: 'error',
            content: t('mp.label.재검토 사유를 입력해 주세요.', '재검토 사유를 입력해 주세요.'),
          });
          return;
        }
        msg = t('mp.label.개발중 상태로 변경하시겠습니까?', '개발중 상태로 변경하시겠습니까?');
      }
      if (ItemStatCd.McrcReview === stat) {
        msg = t('mp.label.분과위 심의를 요청 하시겠습니까?', '분과위 심의를 요청 하시겠습니까?');
      }
      if (ItemStatCd.Regist === stat) {
        // FastTrack인 경우
        if (mpItem?.fstrYn === 'Y') {
          msg = t(
            'mp.label.[설비변경점/수평전개] Item 등록 상태로 변경하시겠습니까?',
            '[설비변경점/수평전개] Item 등록 상태로 변경하시겠습니까?'
          );
        } else {
          msg = t(
            'mp.label.[설비변경점] Item 등록 상태로 변경하시겠습니까?',
            '[설비변경점] Item 등록 상태로 변경하시겠습니까?'
          );
        }
      }
    }
    // 설비변경점인 경우
    else if (MpItemTpCd.MP === mpItem?.mpItemTpCd) {
      // TODO 개발완료 (IF 처리 전 임시 버튼)
      if (ItemStatCd.McrcReview === stat) {
        msg = 'IF 처리 전 임시 버튼으로 분과위심의중 상태로 변경됩니다. 변경하시겠습니까?';
      }
      if (ItemStatCd.M4Change === stat) {
        msg = t(
          'mp.label.4M 변경이 진행되며, 4MCC로 정보 연계 됩니다. 진행하시겠습니까?',
          '4M 변경이 진행되며, 4MCC로 정보 연계 됩니다. 진행하시겠습니까?'
        );
      }
      if (ItemStatCd.Regist === stat) {
        msg = t(
          'mp.label.[수평전개] Item 등록 상태로 변경하시겠습니까?',
          '[수평전개] Item 등록 상태로 변경하시겠습니까?'
        );
      }
      if (ItemStatCd.HeHold === stat) {
        if (!rsnCtn) {
          openMessageBar({
            type: 'error',
            content: t(
              'mp.label.수평전개 보류사유를 입력해 주세요.',
              '수평전개 보류사유를 입력해 주세요.'
            ),
          });
          return;
        }
        msg = t(
          'mp.label.수평전개 보류 상태로 변경하시겠습니까?',
          '수평전개 보류 상태로 변경하시겠습니까?'
        );
      }
    }
    // 수평전개 or 설비변경점/수평전개인 경우
    else if (MpItemTpCd.HE === mpItem?.mpItemTpCd || MpItemTpCd.MPHE === mpItem?.mpItemTpCd) {
      if (ItemStatCd.CopReview === stat) {
        if (ItemStatCd.Regist === mpItem?.itemStatCd) {
          // Item 등록 -> 법인 검토중
          msg = t(
            'mp.label.법인검토중 상태로 변경하시겠습니까?',
            '법인검토중 상태로 변경하시겠습니까?'
          );
        }
        /*
        // [25.09.09] 법인 검토중 (Item 상태)상태 이후로는 검토완료 버튼 삭제
        else {
          // Item 상태: 현재 상태 유지
          // 추가된 공장의 상태: 법인검토중 상태로 변경
          msg = t('mp.label.검토완료 처리하시겠습니까?', '검토완료 처리하시겠습니까?');
        }
         */
      }
    }
    // Drop
    if (ItemStatCd.Drop === stat) {
      if (!rsnCtn) {
        openMessageBar({
          type: 'error',
          content: t('mp.label.Drop 사유를 입력해 주세요.', 'Drop 사유를 입력해 주세요.'),
        });
        return;
      }
      msg = t('mp.msg.드랍하시겠습니까?', '드랍하시겠습니까?');
    }
    if (!valid) {
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: msg,
      yesCallback: async () => {
        openLoading(true);

        const tabId = tabMenus[currentTabIndex].id;
        // 기본사항 탭인 경우 자동 저장
        if (tabId === 'INFO') {
          if (!infoRef.current.validate()) {
            return;
          }

          const defaultParam = {
            itemId: mpItem?.itemId,
            itemNm: mpItem?.itemNm,
            itemEngNm: mpItem?.itemEngNm,
            itemCngNm: mpItem?.itemCngNm,
            reqMgrId: mpItem?.reqMgrId,
            reqMgrChgRsnCtn: mpItem?.reqMgrChgRsnCtn,
            mpClsfCd: mpItem?.mpClsfCd,
            mpItemTpCd: mpItem?.mpItemTpCd,
            itemStatCd: mpItem?.itemStatCd,
          } as MpItem;

          const item = infoRef.current.getMpItem();
          const mpItemParam = {
            ...item,
            ...defaultParam,
          };

          await saveMpItem(mpItemParam)
            .then((res) => {
              if (res.successOrNot !== 'Y') {
                openMessageBar({
                  type: 'error',
                  content: t(
                    'com.label.상태 변경 중 오류가 발생했습니다.',
                    '상태 변경 중 오류가 발생했습니다.'
                  ),
                });
                return;
              }
            })
            .finally(() => openLoading(false));
        }

        const param = {
          itemStatCd: stat,
          itemId: mpItem?.itemId,
          rmk: rsnCtn,
          mpDrwList: infoRef.current?.getMpItem().mpDrwList || [], // 25.11.12 제작사양서 해당사항없음 선택 시 FMD IF 연계하지 않고 완료처리하기 위해 도면 List추가
          drwCpltY: infoRef.current?.getMpItem().drwCpltY || '', // 25.11.12 제작사양서 해당사항없음 선택 시 FMD IF 연계하지 않고 완료처리하기 위해 도면 List추가
          reqMgrId: mpItem?.reqMgrId || '',
          tgtCopCd: mpItem?.tgtCopCd || '', // 25.11.19 Fast Track 진행 시 기본정보 수평전개 탭으로 복사
          tgtPlntCd: mpItem?.tgtPlntCd || '', // 25.11.19 Fast Track 진행 시 기본정보 수평전개 탭으로 복사
          resultList: mpItem?.resultList || [], // 25.11.19 Fast Track 진행 시 기본정보 수평전개 탭으로 복사
        };

        await saveMpItemStat(param)
          .then((res) => {
            setMpItem((prev) => ({
              ...prev,
              ...param,
            }));

            openMessageBar({
              type: res.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                res.successOrNot === 'Y'
                  ? t('com.label.저장되었습니다.', '저장되었습니다.')
                  : res.statusCode === 'FAIL_IF'
                  ? t(
                      'com.label.IF 중 오류가 발생했습니다. 시스템담당자에게 문의하세요.',
                      'IF 중 오류가 발생했습니다. 시스템담당자에게 문의하세요.'
                    )
                  : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
            if (locationState?.mode === ManagementMode.CREATE) {
              menuContext.closeCurrentTab(menuContext);
              setTimeout(() => {
                navigate(`/mp/item-management`, {
                  replace: true,
                  state: {
                    upprMnuUrl: '/mp/item',
                    mode: ManagementMode.MODIFY,
                    itemId: param.itemId,
                    mnuId: `${location.pathname}/${ManagementMode.MODIFY}`,
                    mnuNm: t('mp.label.Item 수정', 'Item 수정'),
                  },
                });
              }, 100);
            } else {
              setLocationState((prev) => ({
                ...prev,
                mode: ManagementMode.MODIFY,
                itemId: param.itemId,
              }));
              // 상태바 갱신
              statRef.current?.refresh(param.itemId);
              const isHeTab =
                (MpItemTpCd.HE === mpItem?.mpItemTpCd || MpItemTpCd.MPHE === mpItem?.mpItemTpCd) &&
                currentTabIndex === 1;
              // 수평전개 or 설비변경점/수평전개인 경우 탭 유지
              loadMpItem(param.itemId, !isHeTab);
              if (isHeTab) {
                otherRef.current?.searchMpItemCop();
              }
            }
          })
          .finally(() => openLoading(false));
      },
    });
  };

  const handleTransferItemNm = () => {
    const ref = refLangCd || 'en';
    if (ref === 'en' && !mpItem?.itemEngNm) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.Item 영어명을 입력해주세요.', 'Item 영어명을 입력해주세요.'),
      });
      return;
    }
    if (ref === 'ko' && !mpItem?.itemNm) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.Item 한글명을 입력해주세요.', 'Item 한글명을 입력해주세요.'),
      });
      return;
    }
    if (ref === 'zh' && !mpItem?.itemCngNm) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.Item 중국어명을 입력해주세요.', 'Item 중국어명을 입력해주세요.'),
      });
      return;
    }

    const condition = {
      source: refLangCd,
      message: ref === 'en' ? mpItem?.itemEngNm : ref === 'ko' ? mpItem?.itemNm : mpItem?.itemCngNm,
      targetStr: refLangCds.join(','),
    };

    getTranslatedText(condition).then((res) => {
      if (res && res.successOrNot === 'Y') {
        const str = res.data ?? '';
        if (typeof str === 'string') {
          const result = JSON.parse(str) as Translation;
          if ((result?.translations || []).length > 0) {
            const trans = (result?.translations || []).reduce((acc, cur) => {
              return {
                ...acc,
                [cur['target']]: CommonUtil.unescapeHtml(cur['result']),
              };
            }, {} as any);

            const itemNm = regexItemNm(trans['ko']),
              itemEngNm = regexItemNm(trans['en'], true),
              itemCngNm = regexItemNm(trans['zh']);
            setMpItem((prev) => ({
              ...prev,
              itemNm: itemNm ? itemNm : prev.itemNm,
              itemEngNm: itemEngNm ? itemEngNm : prev.itemEngNm,
              itemCngNm: itemCngNm ? itemCngNm : prev.itemCngNm,
            }));

            setErrors((prev) => ({
              ...prev,
              itemNm: !itemNm,
              itemEngNm: !itemEngNm,
              itemCngNm: !itemCngNm,
            }));
          }
        }
      } else {
        openMessageBar({
          type: 'error',
          content:
            typeof res.data === 'string'
              ? res.data
              : t('com.msg.오류가 발생하였습니다.', '오류가 발생하였습니다.'),
        });
      }
    });
  };

  return (
    <>
      <TableContainer css={tb.table} style={{ marginBottom: '24px' }}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="row" rowSpan={3}>
                <span>{t('mp.label.Item 명', 'Item 명')}</span>
              </TableCell>
              <TableCell className="odd">
                <span className="dot">{t('mp.label.표준(영어)', '표준(영어)')}</span>
              </TableCell>
              <TableCell className="noBg" colSpan={6}>
                {isEditable('itemEngNm') ? (
                  <CustomInputWithSearch
                    name="itemEngNm"
                    placeholder={String(
                      t('mp.msg.Item 영문명을 입력해 주세요.', 'Item 영문명을 입력해 주세요.')
                    )}
                    value={mpItem?.itemEngNm}
                    onChange={handleItemChange}
                    isError={errors?.itemEngNm}
                    msgError={String(
                      t('mp.msg.Item 영문명을 입력해 주세요.', 'Item 영문명을 입력해 주세요.')
                    )}
                  />
                ) : (
                  mpItem?.itemEngNm
                )}
              </TableCell>
              <TableCell
                className={`odd ${isEditable('itemEngNm') ? '' : 'noBB'}`}
                rowSpan={isEditable('itemEngNm') ? 1 : 2}
              >
                {isEditable('itemEngNm') ? (
                  <ControlBtnGroup>
                    <ComboBox
                      options={code?.langCd || []}
                      style={{ width: '70px' }}
                      defaultValue={refLangCd}
                      onChange={(value) => setRefLangCd(value)}
                    />
                    <Button
                      css={IconButton.button}
                      className="icoNone"
                      onClick={handleTransferItemNm}
                    >
                      {t('mp.label.번역', '번역')}
                    </Button>
                  </ControlBtnGroup>
                ) : (
                  <span>{t('mp.label.Item 번호', 'Item 번호')}</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span>{t('mp.label.한글', '한글')}</span>
              </TableCell>
              <TableCell colSpan={6}>
                {isEditable('itemNm') ? (
                  <CustomInputWithSearch
                    name="itemNm"
                    placeholder={String(
                      t('mp.msg.Item 명을 입력해 주세요.', 'Item 명을 입력해 주세요.')
                    )}
                    value={mpItem?.itemNm}
                    onChange={handleItemChange}
                    isError={errors?.itemNm}
                    msgError={String(
                      t('mp.msg.Item 명을 입력해 주세요.', 'Item 명을 입력해 주세요.')
                    )}
                  />
                ) : (
                  mpItem?.itemNm
                )}
              </TableCell>
              {isEditable('itemEngNm') && (
                <TableCell className="odd noBB">
                  <span>{t('mp.label.Item 번호', 'Item 번호')}</span>
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell>
                <span>{t('mp.label.중국어', '중국어')}</span>
              </TableCell>
              <TableCell colSpan={6}>
                {isEditable('itemCngNm') ? (
                  <CustomInputWithSearch
                    name="itemCngNm"
                    placeholder={String(
                      t('mp.msg.Item 중국어명을 입력해 주세요.', 'Item 중국어명을 입력해 주세요.')
                    )}
                    value={mpItem?.itemCngNm}
                    onChange={handleItemChange}
                    isError={errors?.itemCngNm}
                    msgError={String(
                      t('mp.msg.Item 중국어명을 입력해 주세요.', 'Item 중국어명을 입력해 주세요.')
                    )}
                  />
                ) : (
                  mpItem?.itemCngNm
                )}
              </TableCell>
              <TableCell className="noBg BL">{mpItem?.itemId}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={2}>
                <span className="dot">{t('mp.label.분류', '분류')}</span>
              </TableCell>
              <TableCell>
                {/* 신규개선 > 신규등록, Item 등록인 경우에만 수정 가능 */}
                {mpItem?.mpItemTpCd === MpItemTpCd.NI &&
                (!mpItem?.itemStatCd || mpItem?.itemStatCd === 'MP001') ? (
                  <>
                    <ComboBox
                      options={code?.mpClsfCd || []}
                      placeholder={String(t('com.label.선택', '선택'))}
                      defaultValue={mpItem?.mpClsfCd || ''}
                      onChange={(value) => {
                        setMpItem((prev) => ({ ...prev, mpClsfCd: value }));
                        setErrors((prev) => ({ ...prev, mpClsfCd: false })); //error msg 안없어져서 추가함
                        // changeSearchConditionByMpClsfCd(value, mpItem?.eqclId);
                      }}
                      isError={errors?.mpClsfCd}
                      msgError={String(t('mp.msg.분류를 선택해 주세요.', '분류를 선택해 주세요.'))}
                    />
                  </>
                ) : (
                  mpItem?.mpClsfCdNm
                )}
              </TableCell>
              <TableCell>
                <span className="dot">{t('mp.label.담당자', '담당자')}</span>
              </TableCell>
              <TableCell>
                <FindInputBtnSet>
                  <CustomInputWithSearch
                    name="reqMgrNm"
                    className="find"
                    placeholder={String(
                      t('mp.label.담당자를 입력해 주세요.', '담당자를 입력해 주세요.')
                    )}
                    value={displayCondition?.reqMgrNm}
                    readOnly={!isEditable('reqMgrId')}
                    onChange={handleDisplayConditionChange}
                    onSearchClick={() => {
                      if (isEditable('reqMgrId')) {
                        setOpenElmEmpPopup(true);
                      } else {
                        setOpenUserReadModal(true);
                      }
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        setOpenElmEmpPopup(true);
                      }
                    }}
                    isError={errors?.reqMgrId}
                    msgError={String(
                      t('mp.label.담당자를 선택해 주세요.', '담당자를 선택해 주세요.')
                    )}
                  />
                  <Button
                    css={IconButton.button}
                    className="docu"
                    onClick={() => setOpenMpMgrChgHistoryPopup(true)}
                  />
                </FindInputBtnSet>
              </TableCell>
              <TableCell>
                <span>{t('mp.label.진행단계', '진행단계')}</span>
              </TableCell>
              <TableCell>{mpItemTpCdNm || mpItem?.mpItemTpCd}</TableCell>
              <TableCell>
                <span>{t('mp.label.상태', '상태')}</span>
              </TableCell>
              <TableCell>
                <TextBtnSet>
                  {mpItem?.itemStatCdNm || t('mp.label.신규등록', '신규등록')}
                  {mpItem?.itemStatCdNm && (
                    <Button
                      css={IconButton.button}
                      className="popup"
                      onClick={() => setMpItemStatHisModalOpen(true)}
                    />
                  )}
                </TextBtnSet>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <MpItemStatProcess ref={statRef} mpItemId={mpItem?.itemId} />
      {mpItem?.itemId && (
        <div>
          <div css={tabs.wrap}>
            <Tabs
              value={currentTabIndex}
              onChange={(e, newValue) => {
                setCurrentTabIndex(newValue);
                setChangeTab(true);
              }}
              css={tabs.tab}
            >
              {tabMenus.map((o, idx) => (
                <Tab
                  key={o.id}
                  label={o.name}
                  id={`mp-tab-${idx}`}
                  aria-controls={`mp-tabpanel-${idx}`}
                />
              ))}
              {mpItemStatGuideTxt && (
                <SubTitleGroup>
                  <div
                    className="info default"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mpItemStatGuideTxt) }}
                  ></div>
                </SubTitleGroup>
              )}
              {/*
              <SubTitleGroup>
                <div className="info primary">
                  {t(
                    'mp.label.보고서 및 회의록은 템플릿을 활용하여 작성해주세요.',
                    '보고서 및 회의록은 템플릿을 활용하여 작성해주세요.'
                  )}
                </div>
              </SubTitleGroup>
              */}
            </Tabs>
            <div css={tabs.btn}>
              {/*
              <Button
                css={IconButton.button}
                className="bookmark"
                disableRipple
                onClick={(e) => {
                  setMpItemTplPopup(true);
                }}
              >
                {t('mp.button.보고서/회의록 템플릿', '보고서/회의록 템플릿')}
              </Button>
              */}
              {tabButtons.map((button) => {
                return renderTabButtons(button);
              })}
            </div>
          </div>
          <TabPanel value={currentTabIndex} index={currentTabIndex}>
            {currentTabIndex === 0 ? (
              <MpItemInfoContent
                ref={infoRef}
                defaultMpItem={mpItem}
                mode={locationState?.mode}
                isCopy={!!locationState?.copyTarget}
                isEditable={isEditable}
                onRefresh={() => {
                  // 상태바 갱신
                  statRef.current?.refresh(mpItem.itemId);
                  loadMpItem(mpItem.itemId);
                }}
              />
            ) : (
              <MpItemOtherContent
                ref={otherRef}
                itemTpCd={tabMenus[currentTabIndex]?.id}
                defaultMpItem={mpItem}
                itemId={mpItem.itemId}
                mpClsfCds={mpItem.mpClsfCd}
                isRefresh={isRefresh}
                condition={locationState}
                changeTab={changeTab}
                onRefresh={() => {
                  // 상태바 갱신
                  statRef.current?.refresh(mpItem.itemId);
                  loadMpItem(mpItem.itemId);
                }}
                onChangeRefresh={() => {
                  if (isRefresh) {
                    setRefresh(false);
                  }
                }}
              />
            )}
          </TabPanel>
        </div>
      )}

      {isOpenIprvPurpChgPopup && (
        <MpIprvPurpChgPopUp
          open={isOpenIprvPurpChgPopup}
          close={() => setOpenIprvPurpChgPopup(false)}
          title={t('com.label.개선목적변경', '개선목적변경')}
          /*
          // [25.07.30] 개선목적 Level1 옵션1값 조건 사용안함 (ex. CMN, UT 등)
          initParam={{
            lv1OptVal: 'CMN', // 분류 옵션
          }}
          */
          onCallbackData={(value) => {
            const param = {
              itemId: mpItem?.itemId,
              ecoYn: value?.ecoYn,
              iprvClsfLv1Cd: value?.iprvClsfLv1Cd,
              iprvClsfLv2Cd: value?.iprvClsfLv2Cd,
              iprvClsfLv3Cd: value?.iprvClsfLv3Cd,
              rmk: value?.rmk,
            } as MpItem;
            saveIprvClsf(param).then((res) => {
              if (res.successOrNot === 'Y') {
                openMessageBar({
                  type: 'confirm',
                  content: t('com.label.저장되었습니다.', '저장되었습니다.'),
                });
                loadMpItem(mpItem?.itemId);
              }
            });
          }}
        />
      )}

      {isOpenSwitchToEcPopup && (
        <MpIprvPurpChgPopUp
          open={isOpenSwitchToEcPopup}
          close={() => setOpenSwitchToEcPopup(false)}
          title={t('com.label.개선목적변경', '개선목적변경')}
          isSwitchToEc={true}
          /*
          // [25.07.30] 개선목적 Level1 옵션1값 조건 사용안함 (ex. CMN, UT 등)
          initParam={{
            lv1OptVal: 'CMN', // 분류 옵션
          }}
          */
          onCallbackData={(value) => {
            const param = {
              itemId: mpItem?.itemId,
              ecoYn: value?.ecoYn,
              iprvClsfLv1Cd: value?.iprvClsfLv1Cd,
              iprvClsfLv2Cd: value?.iprvClsfLv2Cd,
              iprvClsfLv3Cd: value?.iprvClsfLv3Cd,
              rmk: value?.rmk,
            } as MpItem;
            switchToEc(param).then((res) => {
              if (res.successOrNot === 'Y') {
                openMessageBar({
                  type: 'confirm',
                  content: t('com.label.저장되었습니다.', '저장되었습니다.'),
                });
                loadMpItem(res.data);
              }
            });
          }}
        />
      )}

      {isOpenRemarkPopup && (
        <MpRemarkPopUp
          title={remarkPopupCondition?.title || '사유 입력'}
          initParam={{
            type: remarkPopupCondition?.type,
            rmk: remarkPopupCondition?.rmk,
            typeValue: remarkPopupCondition?.typeValue,
          }}
          open={isOpenRemarkPopup}
          onCallbackData={(value) => {
            if ('DROP' === remarkPopupCondition?.type) {
              handleChangeStat(TapButtonType.ButtonDrop, value?.rmk);
            } else if ('DEV_REVIEW' === remarkPopupCondition?.type) {
              handleChangeStat(TapButtonType.ButtonDev, value?.rmk);
            } else if ('DLBR_CPLT' === remarkPopupCondition?.type) {
              if ('PROG' === value?.typeValue) {
                // 수평전개 진행
                handleChangeStat(ItemStatCd.Regist, value?.rmk);
              } else if ('HOLD' === value?.typeValue) {
                // 수평전개 보류
                handleChangeStat(ItemStatCd.HeHold, value?.rmk);
              } else {
                openMessageBar({
                  type: 'error',
                  content: t('mp.label.유형을 확인해 주세요.', '유형을 확인해 주세요.'),
                });
                return;
              }
            } else if ('HE_REPROCESS' === remarkPopupCondition?.type) {
              // 수평전개 진행
              handleChangeStat(ItemStatCd.Regist, value?.rmk);
            }
          }}
          close={() => setOpenRemarkPopup(false)}
        />
      )}
      {isOpenElmEmpPopup && (
        <UserMultiModal
          open={isOpenElmEmpPopup}
          close={() => setOpenElmEmpPopup(false)}
          title={t('com.label.사용자 조회', '사용자 조회')}
          condition={{
            // userId: mpItem?.reqMgrId,
            empNm: displayCondition?.reqMgrNm,
            rmk: mpItem?.reqMgrChgRsnCtn,
          }}
          singleSelect={true}
          rmk={isShowRmk('reqMgrId')}
          isOnlyUsed={true}
          isRequireRmk={isRequireRmk('reqMgrId')}
          onCallback={(callbackData: any) => {
            if (!callbackData || callbackData.userId === '') {
              openMessageBar({
                type: 'warning',
                content: t('com.msg.선택된 사용자가 없습니다.', '선택된 사용자가 없습니다.'),
              });
              return;
            }
            setMpItem((prev) => ({
              ...prev,
              reqMgrId: callbackData.userId,
              reqMgrChgRsnCtn: callbackData?.rmk,
            }));
            setDisplayCondition({
              ...displayCondition,
              reqMgrNm: callbackData.empNm,
            });
          }}
        />
      )}
      {isOpenUserReadModal && (
        <GatingContListPopUp
          open={isOpenUserReadModal}
          close={() => setOpenUserReadModal(false)}
          title={t('mp.label.담당자', '담당자')}
          initParam={mpItem?.reqMgrId}
        />
      )}
      {isMpItemTplPopup && (
        <MpItemTemplatePopUp open={isMpItemTplPopup} close={() => setMpItemTplPopup(false)} />
      )}
      {isOpenMpItemCopModal && mpItem?.itemId && (
        <MpItemCopModal
          open={isOpenMpItemCopModal}
          close={() => setOpenMpItemCopModal(false)}
          itemId={mpItem?.itemId}
          isReadOnly={false}
          onCallback={() => {
            // setRefresh(true);
            // otherRef.current?.reload(
            //   state?.itemId,
            //   state?.itemTpCd,
            //   state?.copCd,
            //   state?.plntCd,
            //   state?.lnId
            // );
            otherRef.current?.searchMpItemCop();
          }}
        />
      )}
      {isMpItemStatHisModalOpen && mpItem?.itemId && (
        <MpItemStatHisModal
          open={isMpItemStatHisModalOpen}
          close={() => setMpItemStatHisModalOpen(false)}
          mpItemId={mpItem?.itemId} // MP품목ID
          mcrcIds={mpItem?.mcrcIds || ''}
        />
      )}

      {isOpenMpMgrChgHistoryPopup && (
        <MpMgrChgHistoryPopUp
          open={isOpenMpMgrChgHistoryPopup}
          close={() => setOpenMpMgrChgHistoryPopup(false)}
          title={String(t('mp.label.담당자', '담당자'))}
          condition={{
            mpItemId: mpItem?.itemId,
            mgrTpCd: 'MGR',
          }}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`mp-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default MpItemManagementPage;
